import {
  Box,
  Chip,
  ClickAwayListener,
  Divider,
  Paper,
  Stack,
  styled,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { getFullName } from '@provision/formatting-utils';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  DynamicIcon,
  PartnerTimeZoneTooltip,
  PlatformUserAvatar,
  Tooltip,
} from 'src/components';
import { ConversationParticipant, Partner } from 'src/graphql';
import { PlatformUser } from 'src/graphql/entities/platform-user';
import { useCopyToClipboard, useDisplayTime } from 'src/utils/hooks';

const RichTooltip = styled(Tooltip)(({ theme }) => ({
  backgroundColor: 'transparent',
  '& .MuiTooltip-tooltip': {
    padding: 0,
    backgroundColor: 'transparent',
    maxWidth: 320,
    borderRadius: theme.shape.borderRadius,
  },
  '& .MuiTooltip-arrow': {
    color: theme.palette.background.paper,
  },
}));

type UserInfoTooltipProps = {
  sender: ConversationParticipant | null;
  partnerId?: number | null;
  isOnline?: boolean;
  children: ReactNode;
  partner?: Partner;
  senderTz: string;
  senderUserGroups: { id: string; name: string }[];
};

export const UserInfoTooltip = ({
  sender,
  partnerId,
  isOnline,
  children,
  partner,
  senderTz,
  senderUserGroups,
}: UserInfoTooltipProps) => {
  const { displayTime } = useDisplayTime({ tz: senderTz || partner?.timeZone });
  const name = sender ? getFullName(sender) : '';
  const { copyToClipboard } = useCopyToClipboard();
  const platformUserSender = sender as PlatformUser;

  const email: string | undefined = platformUserSender?.email ?? undefined;
  const company: string | undefined = partner?.businessName;

  const nowIso = new Date().toISOString();

  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');

  const [open, setOpen] = useState(false);
  const hideTimer = useRef<number | null>(null);
  const showTimer = useRef<number | null>(null);

  const clearHide = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const clearShow = () => {
    if (showTimer.current) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  };

  const openNow = () => {
    clearShow();
    clearHide();
    setOpen(true);
  };

  const closeNow = () => {
    clearHide();
    clearShow();
    setOpen(false);
  };

  const closeSoon = () => {
    clearHide();
    clearShow();
    hideTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  const openSoon = () => {
    if (isTouch) {
      // on touch, toggle happens on click
      openNow();
    } else {
      clearShow();
      clearHide();
      showTimer.current = window.setTimeout(() => setOpen(true), 300);
    }
  };

  useEffect(
    () => () => {
      clearHide();
      clearShow();
    },
    [],
  );

  const trigger = (
    <span
      onClick={isTouch ? () => setOpen((o) => !o) : undefined}
      onMouseEnter={!isTouch ? openSoon : undefined}
      onMouseLeave={!isTouch ? closeSoon : undefined}
      onFocus={!isTouch ? openSoon : undefined}
      onBlur={!isTouch ? closeSoon : undefined}
    >
      {children}
    </span>
  );

  const tooltip = (
    <RichTooltip
      placement="right-start"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      open={open}
      onClose={closeNow}
      disableInteractive={false}
      componentsProps={{
        tooltip: {
          sx: { padding: 0, backgroundColor: 'transparent', pointerEvents: 'auto' },
          onMouseEnter: !isTouch ? openNow : undefined,
          onMouseLeave: !isTouch ? closeSoon : undefined,
        },
      }}
      title={
        <Paper
          elevation={4}
          sx={{
            p: 1.5,
            borderRadius: 1,
            maxWidth: 'calc(100vw - 32px)',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.25}>
            <PlatformUserAvatar
              name={name}
              partnerId={platformUserSender?.partnerId}
              avatarFileKey={platformUserSender?.avatarFileKey}
              sx={{ width: 40, height: 40 }}
            />
            <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  noWrap
                  sx={{ cursor: 'pointer', flex: 1, minWidth: 0 }}
                  onClick={() => copyToClipboard(name)}
                >
                  {name}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} flex="0 0 auto">
                  <Box
                    component="span"
                    sx={{
                      width: (theme) => theme.spacing(1),
                      height: (theme) => theme.spacing(1),
                      borderRadius: '50%',
                      bgcolor: (theme) =>
                        isOnline
                          ? theme.palette.custom.OV.sys.success
                          : theme.palette.action.disabled,
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {isOnline ? 'Online' : 'Offline'}
                  </Typography>
                </Stack>
              </Stack>
              {!!email && (
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ cursor: 'pointer' }}
                  onClick={() => copyToClipboard(email)}
                >
                  {email}
                </Typography>
              )}
              {!!company && (
                <Typography variant="body2" noWrap>
                  {company}
                </Typography>
              )}
            </Stack>
          </Stack>

          <Divider sx={{ my: 1.25 }} />

          <Stack spacing={1}>
            {!!senderUserGroups?.length && (
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                flexWrap="wrap"
                sx={{ py: 0.25 }}
              >
                {senderUserGroups.map((i) => (
                  <Chip
                    key={i.id}
                    size="small"
                    label={i.name}
                    sx={{
                      height: '1.5rem',
                      color: (theme) => theme.palette.custom.OV.sys.onSecondary,
                      backgroundColor: (theme) => theme.palette.custom.OV.sys.secondary,
                    }}
                  />
                ))}
              </Stack>
            )}
            <Stack direction="row" alignItems="center" spacing={1}>
              <DynamicIcon name="schedule" sx={{ fontSize: 18, color: 'text.secondary' }} />
              <PartnerTimeZoneTooltip date={nowIso} partnerId={partnerId}>
                <Typography variant="body2">{displayTime(nowIso)} local time</Typography>
              </PartnerTimeZoneTooltip>
            </Stack>
          </Stack>
        </Paper>
      }
    >
      <span style={{ display: 'inline-flex', order: 0 }}>{trigger}</span>
    </RichTooltip>
  );

  return <ClickAwayListener onClickAway={closeNow}>{tooltip}</ClickAwayListener>;
};
